"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"
import { toast } from "sonner"

import { ConfirmDialog } from "@/components/dialogs"

const CONNECT_NOTICE =
  "Once you connect, Slant starts importing your past emails and calendar events. This first sync usually takes up to 24 hours, depending on the size of your mailbox — you can keep working while it runs and track progress from the badge in the top bar."

// Fire after an email integration successfully connects, to reinforce that the backfill is
// running and where to watch it.
export function showEmailConnectedToast() {
  toast.success("Email connected", {
    description:
      "We're importing your emails and calendar now — this usually takes up to 24 hours. Track progress from the badge in the top bar.",
  })
}

// Wraps an email-connect action with a one-time expectation-setting dialog (the backfill can
// take up to 24 hours) shown before the OAuth redirect. Call `requestConnect(provider)` from
// connect buttons instead of connecting directly, and render `noticeDialog`.
export function useEmailConnectNotice(onConnect: (provider: string) => void) {
  const [pendingProvider, setPendingProvider] = useState<string | null>(null)

  const requestConnect = useCallback((provider: string) => setPendingProvider(provider), [])
  const close = useCallback(() => setPendingProvider(null), [])
  const confirm = useCallback(() => {
    if (pendingProvider) onConnect(pendingProvider)
    setPendingProvider(null)
  }, [pendingProvider, onConnect])

  const noticeDialog = (
    <ConfirmDialog
      open={pendingProvider !== null}
      onOpenChange={(open) => {
        if (!open) close()
      }}
      title={"Connecting your email"}
      description={CONNECT_NOTICE}
      dismiss={"explicit"}
      action={{ label: "Continue", onClick: confirm }}
      cancel={{ onClick: close }}
    />
  )

  return { requestConnect, noticeDialog }
}

// Fires the success toast once when a connect flow returns to the current page with
// ?email=connected, then strips the param. For connect surfaces that return to an arbitrary
// URL (e.g. the inline email overlay / composer); the onboarding and settings flows that
// return to a fixed surface confirm there instead.
export function useEmailConnectedReturnToast() {
  const searchParams = useSearchParams()
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current || searchParams.get("email") !== "connected") return
    firedRef.current = true

    const params = new URLSearchParams(searchParams.toString())
    params.delete("email")
    const query = params.toString()
    window.history.replaceState({}, "", query ? `${window.location.pathname}?${query}` : window.location.pathname)

    // Defer so the provider-level <Toaster /> has subscribed before the toast fires.
    setTimeout(showEmailConnectedToast, 0)
  }, [searchParams])
}

// Drop-in null component for mounting the return-toast handler in a layout.
export function EmailConnectedReturnToast() {
  useEmailConnectedReturnToast()
  return null
}
